import {
	all,
	call,
	delay,
	put,
	select,
	takeEvery,
	takeLatest,
} from "redux-saga/effects";
import {
	Types,
	OperationalDashboardActions,
	OperationalIndicatorRequest,
	PatientContactUpdateRequest,
	TodayFocusActionRequest,
	TodayFocusQueueKey,
} from "../ducks/operationalDashboard";
import { AuthActions } from "../ducks/auth";
import { UserProps } from "../../interfaces/pacientes.interface";
import { sendErrorSentryOrLocalHost } from "../../utils/funcs";
import { AmplitudeLog } from "../../utils/amplitude";
import moment from "moment";
import {
	fetchTodayFocus,
	registerTodayFocusEvent,
	TodayFocusApiError,
	todayFocusItemIdentity,
} from "../../pages/OperationalDashboard/services/todayFocusService";
import {
	registerWhatsAppContact,
	toOperationalLastContact,
} from "../../pages/OperationalDashboard/services/patientContactService";
import { fetchFullPatientForDashboard } from "../../pages/OperationalDashboard/services/clinicalDashboardService";

export function* registerAgendaInterest(): any {
	const user_logado: UserProps = yield select((state: any) => state.auth.user);

	if (!user_logado || !user_logado.token) {
		yield put(
			OperationalDashboardActions.agenda_interest_failure(
				"Usuário não autenticado ou token ausente."
			)
		);
		return;
	}

	async function apiCall() {
		try {
			const requestOptions = {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: "Bearer " + user_logado.token,
				},
				body: JSON.stringify({
					txDataHora: moment(new Date())
						.format("YYYY-MM-DD HH:mm:ss")
						.toString(),
				}),
			};
			const res = await fetch(
				`${process.env.REACT_APP_BASE_URL_V7}dashboard/operacional/agenda/interesse`,
				requestOptions
			);
			const response = await res.json();
			return response;
		} catch (e) {
			sendErrorSentryOrLocalHost(e, {
				message:
					"Erro ao registrar interesse na agenda - registerAgendaInterest",
				tags: {
					endpoint: "/dashboard/operacional/agenda/interesse",
					file: "src/store/sagas/operationalDashboard.ts",
				},
			});
			throw e;
		}
	}

	try {
		const response = yield call(apiCall);
		if (response && (response.status === "success" || response.code === 0)) {
			// Trigger Amplitude analytics event with user name and email
			AmplitudeLog("agenda_interesse_click", {
				body: {
					email: user_logado.USUARIO_EMAIL || "",
					nome: user_logado.USUARIO_NOME || "",
				},
			});

			yield put(OperationalDashboardActions.agenda_interest_success());
		} else {
			const errMsg =
				response?.message || "Erro ao registrar interesse na agenda.";
			yield put(OperationalDashboardActions.agenda_interest_failure(errMsg));
		}
	} catch (e: any) {
		yield put(
			OperationalDashboardActions.agenda_interest_failure(
				e.message || "Erro na requisição."
			)
		);
	}
}

export function* getOperationalResumo(): any {
	const user_logado: UserProps = yield select((state: any) => state.auth.user);

	if (!user_logado || !user_logado.token) {
		yield put(
			OperationalDashboardActions.operational_resumo_failure(
				"Usuário não autenticado ou token ausente."
			)
		);
		return;
	}

	async function apiCall() {
		try {
			const requestOptions = {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: "Bearer " + user_logado.token,
				},
				body: JSON.stringify({}),
			};
			const res = await fetch(
				`${process.env.REACT_APP_BASE_URL_V7}dashboard/operacional/resumo`,
				requestOptions
			);
			const response = await res.json();
			return response;
		} catch (e) {
			sendErrorSentryOrLocalHost(e, {
				message: "Erro ao buscar resumo operacional - getOperationalResumo",
				tags: {
					endpoint: "/dashboard/operacional/resumo",
					file: "src/store/sagas/operationalDashboard.ts",
				},
			});
			throw e;
		}
	}

	try {
		const response = yield call(apiCall);
		if (response && response.status === "success" && response.code === 0) {
			yield put(
				OperationalDashboardActions.operational_resumo_success(response)
			);
		} else {
			const errMsg =
				response?.message || "Erro ao carregar resumo do painel operacional.";
			yield put(OperationalDashboardActions.operational_resumo_failure(errMsg));
		}
	} catch (e: any) {
		yield put(
			OperationalDashboardActions.operational_resumo_failure(
				e.message || "Erro na requisição."
			)
		);
	}
}

const indicatorResponseKey = {
	PRONTOS_REAVALIACAO: "prontosReavaliacao",
	PACIENTES_INATIVOS: "pacientesInativos",
	PACIENTES_AVALIADOS: "pacientesAvaliados",
};

export const buildPatientContactUpdateBody = (
	token: string,
	patientId: number | string,
	phone: string,
	txDataHora: string
) => ({
	"0": { TOKEN: token },
	"1": {
		ID_USUARIO: Number(patientId),
		USUARIO_CONTATO: phone,
		txDataHora,
	},
});

export function* getOperationalIndicator({ payload }: any): any {
	const request = payload as OperationalIndicatorRequest;
	const user_logado: UserProps = yield select((state: any) => state.auth.user);

	if (!user_logado?.token) {
		yield put(
			OperationalDashboardActions.operational_indicator_failure(
				request.indicator,
				"Usuário não autenticado ou token ausente."
			)
		);
		return;
	}

	async function apiCall() {
		const body: any = {
			INDICADOR: request.indicator,
			PAGINA: request.page || 1,
			LIMITE: request.limit || 20,
		};
		if (request.search?.trim()) body.BUSCA = request.search.trim();

		try {
			const res = await fetch(
				`${process.env.REACT_APP_BASE_URL_V7}dashboard/operacional/resumo`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: "Bearer " + user_logado.token,
					},
					body: JSON.stringify(body),
				}
			);
			return await res.json();
		} catch (error) {
			sendErrorSentryOrLocalHost(error, {
				message: "Erro ao buscar indicador do painel operacional",
				tags: {
					endpoint: "/dashboard/operacional/resumo",
					file: "src/store/sagas/operationalDashboard.ts",
				},
			});
			throw error;
		}
	}

	try {
		const response = yield call(apiCall);
		if (response?.status === "success" && response?.code === 0) {
			const key = indicatorResponseKey[request.indicator];
			yield put(
				OperationalDashboardActions.operational_indicator_success(
					request.indicator,
					response[key],
					request.append === true
				)
			);
		} else {
			yield put(
				OperationalDashboardActions.operational_indicator_failure(
					request.indicator,
					response?.message || "Erro ao carregar pacientes do indicador."
				)
			);
		}
	} catch (error: any) {
		yield put(
			OperationalDashboardActions.operational_indicator_failure(
				request.indicator,
				error?.message || "Erro na requisição."
			)
		);
	}
}

export function* updatePatientContact({ payload }: any): any {
	const request = payload as PatientContactUpdateRequest;
	const user: UserProps = yield select((state: any) => state.auth.user);

	if (!user?.token) {
		yield put(
			OperationalDashboardActions.patient_contact_update_failure(
				request.patientId,
				"Usuário não autenticado ou token ausente."
			)
		);
		if (request.onFailure) yield call(request.onFailure);
		return;
	}

	async function apiCall() {
		try {
			const response = await fetch(
				`${process.env.REACT_APP_BASE_URL_V7}usuarios/paciente_editar`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: "Bearer " + user.token,
					},
					body: JSON.stringify(
						buildPatientContactUpdateBody(
							user.token,
							request.patientId,
							request.phone,
							moment().format("YYYY-MM-DD HH:mm:ss")
						)
					),
				}
			);
			return await response.json();
		} catch (error) {
			sendErrorSentryOrLocalHost(error, {
				message: "Erro ao atualizar contato do paciente no painel operacional",
				tags: {
					endpoint: "/usuarios/paciente_editar",
					file: "src/store/sagas/operationalDashboard.ts",
				},
			});
			throw error;
		}
	}

	try {
		const response = yield call(apiCall);
		if (response?.status === "success" && response?.code === 0) {
			yield put(
				OperationalDashboardActions.patient_contact_update_success(
					request.patientId,
					request.phone
				)
			);
			if (request.onSuccess) yield call(request.onSuccess);
		} else {
			yield put(
				OperationalDashboardActions.patient_contact_update_failure(
					request.patientId,
					response?.message || "Não foi possível atualizar o telefone."
				)
			);
			if (request.onFailure) yield call(request.onFailure);
		}
	} catch (error: any) {
		yield put(
			OperationalDashboardActions.patient_contact_update_failure(
				request.patientId,
				error?.message || "Não foi possível atualizar o telefone."
			)
		);
		if (request.onFailure) yield call(request.onFailure);
	}
}

export function* verifyAgendaInterest(): any {
	const user_logado: UserProps = yield select(
		(state: any) => state?.auth?.user
	);

	if (!user_logado || !user_logado?.token) {
		yield put(
			OperationalDashboardActions?.agenda_interest_verify_failure?.(
				"Usuário não autenticado ou token ausente."
			)
		);
		return;
	}

	async function apiCall() {
		try {
			const requestOptions = {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: "Bearer " + user_logado?.token,
				},
			};
			const res = await fetch(
				`${process.env.REACT_APP_BASE_URL_V7}dashboard/operacional/agenda/interesse/verificar`,
				requestOptions
			);
			const response = await res?.json?.();
			return response;
		} catch (e) {
			sendErrorSentryOrLocalHost?.(e, {
				message: "Erro ao verificar interesse na agenda - verifyAgendaInterest",
				tags: {
					endpoint: "/dashboard/operacional/agenda/interesse/verificar",
					file: "src/store/sagas/operationalDashboard.ts",
				},
			});
			throw e;
		}
	}

	try {
		const response = yield call(apiCall);
		if (response && response?.status === "success") {
			const hasInterest =
				response?.interesse !== null && typeof response?.interesse === "object";
			yield put(
				OperationalDashboardActions?.agenda_interest_verify_success?.(
					hasInterest
				)
			);
		} else {
			yield put(
				OperationalDashboardActions?.agenda_interest_verify_success?.(false)
			);
		}
	} catch (e: any) {
		yield put(
			OperationalDashboardActions?.agenda_interest_verify_success?.(false)
		);
	}
}

const todayFocusErrorMessage = (error: any) => {
	const status = Number(error?.status) || 0;
	if (status === 400)
		return "Não foi possível concluir esta ação. Tente novamente.";
	if (status === 401)
		return "Sua sessão expirou. Entre novamente para continuar.";
	if (status === 403) return "Você não tem permissão para concluir esta ação.";
	if (status === 422)
		return "Não foi possível concluir esta ação. Tente novamente.";
	if (status === 503 || status >= 500) {
		return "O serviço está indisponível. Tente novamente em instantes.";
	}
	return "Não foi possível concluir esta ação. Verifique sua conexão e tente novamente.";
};

export function* getTodayFocus(): any {
	const user: UserProps = yield select((state: any) => state?.auth?.user);
	if (!user?.token) {
		yield put(
			OperationalDashboardActions.today_focus_failure(
				"Sua sessão expirou. Entre novamente para continuar."
			)
		);
		return;
	}

	try {
		const result = yield call(fetchTodayFocus, user.token, 20);
		yield put(OperationalDashboardActions.today_focus_success(result));
	} catch (error: any) {
		if (Number(error?.status) === 401) yield put(AuthActions.auth_logout());
		yield put(
			OperationalDashboardActions.today_focus_failure(
				todayFocusErrorMessage(error)
			)
		);
	}
}

const currentFocusItemMatches = (
	state: any,
	queueKey: TodayFocusQueueKey,
	itemIdentity: string
) =>
	todayFocusItemIdentity(
		state?.operationalDashboard?.todayFocus?.queues?.[queueKey]?.items?.[0]
	) === itemIdentity;

export function* handleTodayFocusAction({ payload }: any): any {
	const request = payload as TodayFocusActionRequest;
	const itemIdentity = todayFocusItemIdentity(request.item);
	const user: UserProps = yield select((state: any) => state?.auth?.user);

	if (!user?.token) {
		yield put(
			OperationalDashboardActions.today_focus_action_failure(
				request,
				itemIdentity,
				"Sua sessão expirou. Entre novamente para continuar."
			)
		);
		if (request.onFailure) yield call(request.onFailure);
		return;
	}

	let result: any = null;
	try {
		if (request.actionKind === "CONTACT_STARTED") {
			const origin =
				request.item.FOCO_TIPO === "INVITE_REEVALUATION"
					? "REEVALUATION"
					: "CRITICAL_ALERT";
			result = yield call(
				registerWhatsAppContact,
				user.token,
				{ ID_USUARIO: request.item.ID_USUARIO, ORIGEM: origin },
				request.idempotencyKey
			);
		} else {
			if (request.actionKind === "REPORT_SHARE_STARTED") {
				result = yield call(
					fetchFullPatientForDashboard,
					user.token,
					request.item.ID_USUARIO
				);
				if (!result) {
					throw new TodayFocusApiError(
						422,
						"Cadastro completo do paciente indisponível."
					);
				}
			}
			yield call(
				registerTodayFocusEvent,
				user.token,
				request.item,
				request.actionKind,
				request.idempotencyKey
			);
		}
	} catch (error: any) {
		const status = Number(error?.status) || 0;
		if (status === 401) yield put(AuthActions.auth_logout());
		if (status === 409) {
			yield put(OperationalDashboardActions.today_focus_invalidate());
			yield put(OperationalDashboardActions.today_focus_request());
		} else {
			yield put(
				OperationalDashboardActions.today_focus_action_failure(
					request,
					itemIdentity,
					todayFocusErrorMessage(error)
				)
			);
		}
		if (request.onFailure) yield call(request.onFailure);
		return;
	}

	const stateAfterRequest = yield select((state: any) => state);
	if (
		!currentFocusItemMatches(stateAfterRequest, request.queueKey, itemIdentity)
	) {
		if (request.onFailure) yield call(request.onFailure);
		return;
	}

	const queue =
		stateAfterRequest.operationalDashboard.todayFocus.queues[request.queueKey];
	yield put(
		OperationalDashboardActions.today_focus_action_confirmed(
			request.queueKey,
			itemIdentity,
			request.actionKind === "DISMISSED" ? "dismissed" : "success"
		)
	);
	yield delay(400);
	yield put(
		OperationalDashboardActions.today_focus_action_success(
			request.queueKey,
			itemIdentity
		)
	);
	if (request.actionKind === "CONTACT_STARTED") {
		yield put(
			OperationalDashboardActions.patient_whatsapp_contact_register_success(
				request.item.ID_USUARIO,
				toOperationalLastContact(result)
			)
		);
	}
	if (queue.items.length === 1 && queue.temProxima) {
		yield put(OperationalDashboardActions.today_focus_request());
	}
	if (request.onSuccess) yield call(request.onSuccess, result);
}

export function* OperationalDashboardSaga(): any {
	yield all([
		takeLatest(Types.AGENDA_INTEREST_REQUEST, registerAgendaInterest),
		takeLatest(Types.AGENDA_INTEREST_VERIFY_REQUEST, verifyAgendaInterest),
		takeLatest(Types.OPERATIONAL_RESUMO_REQUEST, getOperationalResumo),
		takeLatest(Types.OPERATIONAL_INDICATOR_REQUEST, getOperationalIndicator),
		takeEvery(Types.PATIENT_CONTACT_UPDATE_REQUEST, updatePatientContact),
		takeLatest(Types.TODAY_FOCUS_REQUEST, getTodayFocus),
		takeEvery(Types.TODAY_FOCUS_ACTION_REQUEST, handleTodayFocusAction),
	]);
}
